import React, { useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Input,
  Button,
  Box,
  Text,
  VStack,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useCognito } from "../utils/useCognito";

const NpiLookupDrawer = () => {
  const [npi, setNpi] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toast = useToast();
  const { getIdToken } = useCognito();

  const handleSearch = async () => {
    if (!npi || npi.length !== 10) {
      toast({ title: "Enter a valid 10-digit NPI", status: "warning" });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = getIdToken();
      const res = await axios.get(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/lookupNpi",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { number: npi },
        }
      );

      if (res.data.results?.length > 0) {
        setResult(res.data.results[0]);
      } else {
        toast({ title: "No NPI record found.", status: "info" });
      }
    } catch (err) {
      console.error(err);
      toast({ title: "Error fetching NPI data", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, value) => (
    <Box>
      <Text fontWeight="bold" fontSize="sm">{label}</Text>
      <Text fontSize="sm" color="gray.600">{value || "-"}</Text>
    </Box>
  );

  const basic = result?.basic || {};
  const location = result?.addresses?.find(a => a.address_purpose === "LOCATION");
  const mailing = result?.addresses?.find(a => a.address_purpose === "MAILING");
  const taxonomy = result?.taxonomies?.find(t => t.primary) || result?.taxonomies?.[0];
  const official = basic?.authorized_official_first_name ? {
    name: `${basic.authorized_official_first_name} ${basic.authorized_official_last_name}`,
    title: basic.authorized_official_title_or_position,
    phone: basic.authorized_official_telephone_number,
  } : null;

  return (
    <>
      {/* Floating Toggle Button */}
      <IconButton
        icon={isDrawerOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        position="fixed"
        right="0"
        top="50%"
        transform="translateY(-50%)"
        zIndex="1500"
        onClick={() => setIsDrawerOpen(!isDrawerOpen)}
        aria-label="Toggle NPI Drawer"
        borderTopLeftRadius="0"
        borderBottomLeftRadius="0"
        colorScheme="blue"
        boxShadow="lg"
      />

      <Drawer isOpen={isDrawerOpen} placement="right" onClose={() => setIsDrawerOpen(false)} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>NPI Lookup</DrawerHeader>

          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Enter 10-digit NPI"
                value={npi}
                onChange={(e) => setNpi(e.target.value)}
                maxLength={10}
              />
              <Button colorScheme="blue" onClick={handleSearch} isLoading={loading}>
                Search
              </Button>

              {result && (
                <Box mt={4} p={3} border="1px solid #ccc" borderRadius="md">
                  <Text fontWeight="bold" fontSize="md" mb={2}>NPI Registry Result</Text>
                  <VStack align="start" spacing={2}>
                    {renderField("NPI", result.number)}
                    {renderField("Type", result.enumeration_type)}
                    {renderField("Name", basic.first_name ? `${basic.first_name} ${basic.last_name}` : basic.organization_name)}
                    {basic.credential && renderField("Credential", basic.credential)}
                    {taxonomy && renderField("Specialty", taxonomy.desc)}

                    {location && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">Location Address</Text>
                        <Text fontSize="sm" color="gray.600">
                          {location.address_1}, {location.city}, {location.state} {location.postal_code?.slice(0, 5)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Phone: {location.telephone_number} | Fax: {location.fax_number}
                        </Text>
                      </Box>
                    )}

                    {mailing && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">Mailing Address</Text>
                        <Text fontSize="sm" color="gray.600">
                          {mailing.address_1}, {mailing.city}, {mailing.state} {mailing.postal_code?.slice(0, 5)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Phone: {mailing.telephone_number} | Fax: {mailing.fax_number}
                        </Text>
                      </Box>
                    )}

                    {official && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">Authorized Official</Text>
                        <Text fontSize="sm" color="gray.600">{official.name} ({official.title})</Text>
                        <Text fontSize="sm" color="gray.600">Phone: {official.phone}</Text>
                      </Box>
                    )}

                    {result.practiceLocations?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">Practice Locations</Text>
                        {result.practiceLocations.map((loc, idx) => (
                          <Text key={idx} fontSize="sm" color="gray.600">
                            {loc.address_1} {loc.address_2 ? ", " + loc.address_2 : ""}, {loc.city}, {loc.state} {loc.postal_code?.slice(0, 5)}
                          </Text>
                        ))}
                      </Box>
                    )}

                    {result.identifiers?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">Identifiers</Text>
                        {result.identifiers.map((id, idx) => (
                          <Text key={idx} fontSize="sm" color="gray.600">
                            {id.desc}: {id.identifier} {id.issuer && `(${id.issuer})`}
                          </Text>
                        ))}
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default NpiLookupDrawer;
